import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "Centros";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const ListarCentros = ({ refresh }) => {
  const [centrosFormacion, setCentrosFormacion] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();

  // ==============================
  // CONFIG TOKEN
  // ==============================
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // ==============================
  // PERMISOS MÓDULO CENTROS
  // ==============================
  const getModuloCentros = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloCentros = useMemo(() => getModuloCentros(), []);

  const puedeCrear =
    moduloCentros?.puedeCrear === true ||
    moduloCentros?.crear === true ||
    moduloCentros?.create === true;

  const puedeActualizar =
    moduloCentros?.puedeActualizar === true ||
    moduloCentros?.actualizar === true ||
    moduloCentros?.editar === true ||
    moduloCentros?.update === true;

  const puedeEliminar =
    moduloCentros?.puedeEliminar === true ||
    moduloCentros?.eliminar === true ||
    moduloCentros?.delete === true;

  // ==============================
  // VALIDAR SESIÓN
  // ==============================
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  // ==============================
  // ELIMINAR CENTRO
  // ==============================
  const deleteCentros = useCallback(
    async (id) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar centros.",
          "warning"
        );
        return false;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return false;

        setDeletingId(id);

        const { data } = await axios.delete(
          `/api/centrosdeformacion/${id}`,
          authConfig()
        );

        setCentrosFormacion((prev) =>
          prev.filter((centro) => centro._id !== id)
        );

        Swal.fire({
          icon: "success",
          title: data?.message || "Centro eliminado",
          showConfirmButton: false,
          timer: 1500,
        });

        return true;
      } catch (error) {
        console.error("Error al eliminar el centro:", error);

        if (manejarErrorSesion(error)) return false;

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo eliminar el centro.",
          "error"
        );

        return false;
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  // ==============================
  // CARGAR DATOS
  // ==============================
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(
        "/api/centrosdeformacion/listall/",
        authConfig()
      );

      const centros = Array.isArray(data?.centros) ? data.centros : [];

      setCentrosFormacion([...centros].reverse());
    } catch (error) {
      console.error("Error al cargar centros:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message || "No se pudieron cargar los centros.",
        "error"
      );

      setCentrosFormacion([]);
    } finally {
      setLoading(false);
    }
  }, [manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  // ==============================
  // DATA TABLE
  // ==============================
  const dataTabla = useMemo(
    () =>
      centrosFormacion.map((centro) => ({
        ...centro,

        nombreCentro: (
          <div title={centro.nombreCentro}>{centro.nombreCentro}</div>
        ),

        direccionCentro: (
          <div title={centro.direccionCentro}>
            {centro.direccionCentro}
          </div>
        ),

        nombreRegional:
          centro.regionalObjectId?.nombreRegional ||
          "Regional no disponible",

        funciones: (
          <div className="d-flex gap-2 justify-content-end">
            {puedeActualizar && (
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                disabled={loading || deletingId === centro._id}
                onClick={() =>
                  navigate(`/institucional/centro/edit/${centro._id}`)
                }
                title="Editar Centro"
                type="button"
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>
            )}

            {puedeEliminar && (
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                disabled={loading || deletingId === centro._id}
                onClick={() => deleteCentros(centro._id)}
                title="Eliminar Centro"
                type="button"
              >
                {deletingId === centro._id ? (
                  <i className="fa-solid fa-spinner fa-spin text-white"></i>
                ) : (
                  <i className="fa-solid fa-trash"></i>
                )}
              </button>
            )}
          </div>
        ),
      })),
    [
      centrosFormacion,
      puedeActualizar,
      puedeEliminar,
      loading,
      deletingId,
      navigate,
      deleteCentros,
    ]
  );

  const columns = useMemo(
    () => [
      { Header: "Código", accessor: "codCentro" },
      { Header: "Nombre Centro", accessor: "nombreCentro" },
      { Header: "Municipio", accessor: "municipioCentro" },
      { Header: "Dirección", accessor: "direccionCentro" },
      { Header: "Regional", accessor: "nombreRegional" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Centros de Formación
                </h5>

                <small className="text-secondary">
                  Gestión institucional y administración de centros.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  className="btn btn-outline-dark flex-fill"
                  type="button"
                  onClick={() => navigate("/inicio")}
                  title="Volver al inicio"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Inicio
                </button>

                <button
                  className="btn btn-outline-secondary flex-fill"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                  title="Actualizar"
                >
                  <i
                    className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"
                      } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark flex-fill"
                    type="button"
                    disabled={loading}
                    onClick={() => navigate("/institucional/centro/nuevo")}
                    title="Crear Centro"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">Cargando centros…</div>
              </div>
            ) : dataTabla.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={dataTabla} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🏫</div>

                <div className="fw-semibold">
                  No hay centros de formación registrados
                </div>

                <div className="small mb-3">
                  Registra un centro para iniciar la gestión.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => navigate("/institucional/centro/nuevo")}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Registrar centro
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarCentros;